import { Component } from '@theme/component';
import { debounce, onAnimationEnd, prefersReducedMotion, yieldToMainThread } from '@theme/utilities';
import { sectionRenderer } from '@theme/section-renderer';
import { morph } from '@theme/morph';
import { RecentlyViewed } from '@theme/recently-viewed-products';
import { DialogCloseEvent, DialogOpenEvent, DialogComponent } from '@theme/dialog';

/**
 * A custom element that allows the user to search for resources available on the store.
 *
 * @typedef {object} Refs
 * @property {HTMLInputElement} searchInput - The search input element.
 * @property {HTMLElement} predictiveSearchResults - The predictive search results container.
 * @property {HTMLElement} resetButton - The reset button element.
 * @property {HTMLElement[]} [resultsItems] - The search results items elements.
 * @property {HTMLElement} [recentlyViewedWrapper] - The recently viewed products wrapper.
 * @property {HTMLElement[]} [recentlyViewedTitle] - The recently viewed title elements.
 * @property {HTMLElement[]} [recentlyViewedItems] - The recently viewed product items.
 * @extends {Component<Refs>}
 */
class PredictiveSearchComponent extends Component {
  requiredRefs = ['searchInput', 'predictiveSearchResults', 'resetButton'];

  #controller = new AbortController();

  /**
   * @type {AbortController | null}
   */
  #activeFetch = null;

  #emptyStateLoaded = false;
  #initialEmptyState = null;
  #preparedEmptyState = null;
  #prepareEmptyStatePromise = null;
  #searchTimer = null;

  /**
   * Get the dialog component.
   * @returns {DialogComponent | null} The dialog component.
   */
  get dialog() {
    return this.closest('dialog-component');
  }

  #isSearchDialogOpen() {
    const dialog = this.dialog?.refs.dialog;
    return Boolean(dialog?.open || dialog?.classList.contains('search-modal-mobile-open'));
  }

  connectedCallback() {
    super.connectedCallback();

    const { dialog } = this;
    const { signal } = this.#controller;

    if (this.refs.searchInput.value.length > 0) {
      this.#showResetButton();
    }

    this.#initialEmptyState = this.refs.predictiveSearchResults.cloneNode(true);

    // Ao tocar em um resultado, congela atualizações pendentes antes do clique.
    // Isso evita que uma resposta da busca substitua o card entre pointerdown e click.
    this.addEventListener('pointerdown', this.#handleResultPointerDown, { signal });

    if (dialog) {
      document.addEventListener('keydown', this.#handleKeyboardShortcut, { signal });
      dialog.addEventListener(DialogCloseEvent.eventName, this.#handleDialogClose, { signal });
      dialog.addEventListener(DialogOpenEvent.eventName, this.#handleDialogOpen, { signal });

      this.addEventListener('click', this.#handleModalClick, { signal });
    }

    if (RecentlyViewed.getProducts().length > 0) {
      const prepareEmptyState = () => {
        void this.#prepareEmptyState();
      };

      if (typeof window.requestIdleCallback === 'function') {
        window.requestIdleCallback(prepareEmptyState, { timeout: 1500 });
      } else {
        window.setTimeout(prepareEmptyState, 1200);
      }
    } else {
      this.#preparedEmptyState = this.#initialEmptyState.cloneNode(true);
      this.#emptyStateLoaded = true;
    }
  }

  #cancelPendingSearchWork() {
    if (this.#searchTimer) {
      window.clearTimeout(this.#searchTimer);
      this.#searchTimer = null;
    }

    if (this.#activeFetch) {
      this.#activeFetch.abort();
      this.#activeFetch = null;
    }
  }

  /**
   * Prevent a pending predictive-search render from replacing a result while
   * the user is in the middle of tapping it.
   * @param {PointerEvent} event
   */
  #handleResultPointerDown = (event) => {
    const target = event.target;
    if (!(target instanceof Element)) return;

    const card = target.closest('.predictive-search-results__card');
    const resourceLink = card?.querySelector('a.resource-card__link[href]');
    const queryLink = target.closest('.predictive-search-results__pill[href]');

    if (!(resourceLink instanceof HTMLAnchorElement) && !(queryLink instanceof HTMLAnchorElement)) return;

    this.#cancelPendingSearchWork();
  };

  /**
   * Handles clicks within the predictive search modal.
   * Resource cards navigate explicitly so products and collections cannot lose
   * their click if the predictive-search DOM is updating at the same time.
   * @param {MouseEvent} event - The mouse event
   */
  #handleModalClick = (event) => {
    const target = /** @type {HTMLElement} */ (event.target);

    const resourceCard = target.closest('.predictive-search-results__card');
    const resourceLink = resourceCard?.querySelector('a.resource-card__link[href]');

    if (resourceLink instanceof HTMLAnchorElement) {
      // A carousel can intentionally prevent a click after a drag.
      if (event.defaultPrevented) return;

      event.preventDefault();
      this.#cancelPendingSearchWork();
      window.location.assign(resourceLink.href);
      return;
    }

    const isInteractiveElement =
      target instanceof HTMLButtonElement ||
      target instanceof HTMLAnchorElement ||
      target instanceof HTMLInputElement ||
      target.closest('button') ||
      target.closest('a') ||
      target.closest('input');

    if (!isInteractiveElement && this.refs.searchInput) {
      this.refs.searchInput.focus();
    }
  };

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#controller.abort();

    if (this.#searchTimer) {
      window.clearTimeout(this.#searchTimer);
      this.#searchTimer = null;
    }
  }

  /**
   * Handles the CMD+K key combination.
   * @param {KeyboardEvent} event - The keyboard event.
   */
  #handleKeyboardShortcut = (event) => {
    if (event.metaKey && event.key === 'k') {
      this.dialog?.toggleDialog();
    }
  };

  /**
   * Handles the dialog close event.
   */
  #handleDialogClose = () => {
    const hasSearchResults = Boolean(this.refs.predictiveSearchResults.querySelector('[data-search-results]'));

    if (this.refs.searchInput.value.trim().length > 0 || hasSearchResults) {
      this.#resetSearch();
    }
  };

  #handleDialogOpen = () => {
    void this.#renderPreparedEmptyStateAfterPaint();
  };

  async #renderPreparedEmptyStateAfterPaint() {
    if (this.refs.searchInput.value.trim().length > 0) return;
    if (RecentlyViewed.getProducts().length === 0) return;
    if (this.refs.predictiveSearchResults.querySelector('[ref="recentlyViewedWrapper"]')) return;

    // Prioriza a resposta visual ao toque. O conteúdo complementar entra depois do primeiro paint.
    await yieldToMainThread();

    if (!this.#isSearchDialogOpen() || this.refs.searchInput.value.trim().length > 0) return;

    const prepared = await this.#prepareEmptyState();

    if (!prepared || !this.#isSearchDialogOpen() || this.refs.searchInput.value.trim().length > 0) return;

    morph(this.refs.predictiveSearchResults, prepared.cloneNode(true));
    this.#resetScrollPositions();
  }

  async #prepareEmptyState() {
    if (this.#preparedEmptyState) return this.#preparedEmptyState;
    if (this.#prepareEmptyStatePromise) return this.#prepareEmptyStatePromise;

    this.#prepareEmptyStatePromise = (async () => {
      const base = this.#initialEmptyState?.cloneNode(true) ?? this.refs.predictiveSearchResults.cloneNode(true);
      const viewedProducts = RecentlyViewed.getProducts();

      if (viewedProducts.length > 0) {
        const recentlyViewedMarkup = await this.#getRecentlyViewedProductsMarkup();

        if (recentlyViewedMarkup) {
          const parsedRecentlyViewedMarkup = new DOMParser().parseFromString(recentlyViewedMarkup, 'text/html');
          const recentlyViewedProductsHtml = parsedRecentlyViewedMarkup.getElementById('predictive-search-products');

          if (recentlyViewedProductsHtml) {
            for (const child of recentlyViewedProductsHtml.children) {
              if (child instanceof HTMLElement) {
                child.setAttribute('ref', 'recentlyViewedWrapper');
              }
            }

            const existingProductsContainer = base.querySelector('#predictive-search-products');

            if (existingProductsContainer) {
              existingProductsContainer.prepend(...recentlyViewedProductsHtml.children);
            } else {
              const emptyStateInner = base.querySelector('.predictive-search-results__inner');
              emptyStateInner?.prepend(recentlyViewedProductsHtml);
            }
          }
        }
      }

      this.#preparedEmptyState = base;
      this.#emptyStateLoaded = true;
      return base;
    })();

    try {
      return await this.#prepareEmptyStatePromise;
    } finally {
      this.#prepareEmptyStatePromise = null;
    }
  }

  get #allResultsItems() {
    const containers = Array.from(
      this.querySelectorAll(
        '.predictive-search-results__wrapper-queries, ' +
          '.predictive-search-results__wrapper-products, ' +
          '.predictive-search-results__list'
      )
    );

    const allItems = containers
      .flatMap((container) => {
        if (container.classList.contains('predictive-search-results__wrapper-products')) {
          return Array.from(container.querySelectorAll('.predictive-search-results__card'));
        }
        return Array.from(container.querySelectorAll('[ref="resultsItems[]"], .predictive-search-results__card'));
      })
      .filter((item) => item instanceof HTMLElement);

    return /** @type {HTMLElement[]} */ (allItems);
  }

  /**
   * Track whether the last interaction was keyboard-based
   * @type {boolean}
   */
  #isKeyboardNavigation = false;

  get #currentIndex() {
    return this.#allResultsItems?.findIndex((item) => item.getAttribute('aria-selected') === 'true') ?? -1;
  }

  set #currentIndex(index) {
    if (!this.#allResultsItems?.length) return;

    let activeItem = null;

    this.#allResultsItems.forEach((item) => {
      item.classList.remove('keyboard-focus');
    });

    for (const [itemIndex, item] of this.#allResultsItems.entries()) {
      if (itemIndex === index) {
        item.setAttribute('aria-selected', 'true');
        if (this.#isKeyboardNavigation) {
          item.classList.add('keyboard-focus');
        }
        activeItem = item;
      } else {
        item.removeAttribute('aria-selected');
      }
    }

    activeItem?.scrollIntoView({ behavior: prefersReducedMotion() ? 'instant' : 'smooth', block: 'nearest' });
    this.refs.searchInput.focus();
  }

  get #currentItem() {
    return this.#allResultsItems?.[this.#currentIndex];
  }

  #clearCurrentSelection() {
    const selected = this.querySelector('[aria-selected="true"]');
    const keyboardFocus = this.querySelector('.keyboard-focus');

    selected?.removeAttribute('aria-selected');
    keyboardFocus?.classList.remove('keyboard-focus');
  }

  /**
   * Navigate through the predictive search results using arrow keys or close them with the Escape key.
   * @param {KeyboardEvent} event - The keyboard event.
   */
  onSearchKeyDown = (event) => {
    if (event.key === 'Escape') {
      this.#resetSearch();
      return;
    }

    if (!this.#allResultsItems?.length || event.key === 'ArrowLeft' || event.key === 'ArrowRight') {
      return;
    }

    const currentIndex = this.#currentIndex;
    const totalItems = this.#allResultsItems.length;

    switch (event.key) {
      case 'ArrowDown':
        this.#isKeyboardNavigation = true;
        event.preventDefault();
        this.#currentIndex = currentIndex < totalItems - 1 ? currentIndex + 1 : 0;
        break;

      case 'Tab':
        if (event.shiftKey) {
          this.#isKeyboardNavigation = true;
          event.preventDefault();
          this.#currentIndex = currentIndex > 0 ? currentIndex - 1 : totalItems - 1;
        } else {
          this.#isKeyboardNavigation = true;
          event.preventDefault();
          this.#currentIndex = currentIndex < totalItems - 1 ? currentIndex + 1 : 0;
        }
        break;

      case 'ArrowUp':
        this.#isKeyboardNavigation = true;
        event.preventDefault();
        this.#currentIndex = currentIndex > 0 ? currentIndex - 1 : totalItems - 1;
        break;

      case 'Enter': {
        const singleResultContainer = this.refs.predictiveSearchResults.querySelector('[data-single-result-url]');
        if (singleResultContainer instanceof HTMLElement && singleResultContainer.dataset.singleResultUrl) {
          event.preventDefault();
          window.location.href = singleResultContainer.dataset.singleResultUrl;
          return;
        }

        if (this.#currentIndex >= 0) {
          event.preventDefault();
          this.#currentItem?.querySelector('a')?.click();
        } else {
          const searchUrl = new URL(Theme.routes.search_url, location.origin);
          searchUrl.searchParams.set('q', this.refs.searchInput.value);
          searchUrl.searchParams.set('options[fields]', 'title,product_type,variants.title,variants.sku,vendor');
          window.location.href = searchUrl.toString();
        }
        break;
      }
    }
  };

  /**
   * Clears the recently viewed products.
   * @param {Event} event - The event.
   */
  clearRecentlyViewedProducts(event) {
    event.stopPropagation();

    RecentlyViewed.clearProducts();

    const { recentlyViewedItems, recentlyViewedTitle, recentlyViewedWrapper } = this.refs;

    const allRecentlyViewedElements = [...(recentlyViewedItems || []), ...(recentlyViewedTitle || [])];

    if (allRecentlyViewedElements.length === 0) {
      return;
    }

    if (recentlyViewedWrapper) {
      recentlyViewedWrapper.classList.add('removing');

      onAnimationEnd(recentlyViewedWrapper, () => {
        recentlyViewedWrapper.remove();
      });
    }
  }

  /**
   * Reset the search state.
   * @param {boolean} [keepFocus=true] - Whether to keep focus on input after reset
   */
  resetSearch = debounce((keepFocus = true) => {
    if (keepFocus) {
      this.refs.searchInput.focus();
    }
    this.#resetSearch();
  }, 100);

  /**
   * Debounce the search handler to fetch and display search results based on the input value.
   * Reset the current selection index and close results if the search term is empty.
   */
  search = (event) => {
    // If the input is not a text input (like using the Escape key), don't search
    if (!event.inputType) return;

    this.#clearCurrentSelection();

    const immediateTerm = this.refs.searchInput.value.trim();

    if (!immediateTerm.length) {
      if (this.#searchTimer) {
        window.clearTimeout(this.#searchTimer);
        this.#searchTimer = null;
      }

      this.#resetSearch();
      return;
    }

    this.#showResetButton();

    if (this.#searchTimer) {
      window.clearTimeout(this.#searchTimer);
    }

    const delay = window.matchMedia('(max-width: 749px)').matches ? 350 : 200;

    this.#searchTimer = window.setTimeout(() => {
      this.#searchTimer = null;

      const searchTerm = this.refs.searchInput.value.trim();
      if (!searchTerm.length) return;

      this.#getSearchResults(searchTerm);
    }, delay);
  };

  /**
   * Resets scroll positions for search results containers
   */
  #resetScrollPositions() {
    requestAnimationFrame(() => {
      this.refs.predictiveSearchResults.querySelector('.predictive-search-results__inner')?.scrollTo(0, 0);
      this.querySelector('.predictive-search-form__content')?.scrollTo(0, 0);
    });
  }

  /**
   * Fetch search results using the section renderer and update the results container.
   * @param {string} searchTerm - The term to search for
   */
  async #getSearchResults(searchTerm) {
    if (!this.dataset.sectionId) return;

    const isMobile = window.matchMedia('(max-width: 749px)').matches;
    const url = new URL(Theme.routes.predictive_search_url, location.origin);

    url.searchParams.set('q', searchTerm);
    url.searchParams.set('resources[limit_scope]', 'each');
    url.searchParams.set('resources[options][fields]', 'title,product_type,variants.title,variants.sku,vendor');

    // No celular, mantém o payload controlado, mas preserva a experiência completa:
    // sugestões + tecidos + coleções relacionadas. Páginas/artigos continuam fora para poupar DOM.
    if (isMobile) {
      url.searchParams.set('resources[type]', 'product,query,collection');
      url.searchParams.set('resources[limit]', '8');
    }

    const { predictiveSearchResults } = this.refs;
    const abortController = this.#createAbortController();

    sectionRenderer
      .getSectionHTML(this.dataset.sectionId, true, url)
      .then(async (resultsMarkup) => {
        if (!resultsMarkup || abortController.signal.aborted) return;

        // Se o usuário já digitou outra letra, não renderize o resultado antigo.
        // Antes, respostas intermediárias podiam fazer um morph pesado enquanto o usuário continuava digitando.
        if (this.refs.searchInput.value.trim() !== searchTerm) return;

        if (isMobile) {
          // Garante que o caractere digitado seja pintado antes de montar os resultados.
          await new Promise((resolve) => {
            requestAnimationFrame(() => {
              requestAnimationFrame(resolve);
            });
          });

          if (abortController.signal.aborted || this.refs.searchInput.value.trim() !== searchTerm) return;

          const parsed = new DOMParser().parseFromString(resultsMarkup, 'text/html');
          const nextResults = parsed.querySelector('#predictive-search-results');

          if (nextResults) {
            // Resultados da busca são descartáveis; não precisamos comparar nó por nó com morph().
            // A substituição direta reduz bastante o custo de JS em aparelhos fracos.
            predictiveSearchResults.replaceChildren(nextResults);
          } else {
            morph(predictiveSearchResults, resultsMarkup);
          }
        } else {
          morph(predictiveSearchResults, resultsMarkup);
        }

        this.#resetScrollPositions();
      })
      .catch((error) => {
        if (abortController.signal.aborted) return;
        throw error;
      });
  }

  /**
   * Fetch the markup for the recently viewed products.
   * @returns {Promise<string | null>} The markup for the recently viewed products.
   */
  async #getRecentlyViewedProductsMarkup() {
    if (!this.dataset.sectionId) return null;

    const viewedProducts = RecentlyViewed.getProducts();
    if (viewedProducts.length === 0) return null;

    const url = new URL(Theme.routes.search_url, location.origin);
    url.searchParams.set('q', viewedProducts.map(/** @param {string} id */ (id) => `id:${id}`).join(' OR '));
    url.searchParams.set('resources[type]', 'product');

    return sectionRenderer.getSectionHTML(this.dataset.sectionId, true, url);
  }

  #hideResetButton() {
    const { resetButton } = this.refs;

    if (resetButton.style.opacity === '0') return;

    resetButton.style.opacity = '0';
    resetButton.style.pointerEvents = 'none';
    resetButton.setAttribute('aria-hidden', 'true');
    resetButton.setAttribute('tabindex', '-1');
  }

  #showResetButton() {
    const { resetButton } = this.refs;

    if (resetButton.style.opacity === '1') return;

    resetButton.style.opacity = '1';
    resetButton.style.pointerEvents = 'auto';
    resetButton.removeAttribute('aria-hidden');
    resetButton.removeAttribute('tabindex');
  }

  #createAbortController() {
    const abortController = new AbortController();
    if (this.#activeFetch) {
      this.#activeFetch.abort();
    }
    this.#activeFetch = abortController;
    return abortController;
  }

  #resetSearch = () => {
    const { predictiveSearchResults, searchInput } = this.refs;

    this.#clearCurrentSelection();
    searchInput.value = '';
    this.#hideResetButton();

    const template = this.#preparedEmptyState ?? this.#initialEmptyState;

    if (template) {
      morph(predictiveSearchResults, template.cloneNode(true));
      this.#resetScrollPositions();
    }

    if (!this.#emptyStateLoaded && RecentlyViewed.getProducts().length > 0) {
      void this.#prepareEmptyState().then((prepared) => {
        if (!prepared || !this.#isSearchDialogOpen() || this.refs.searchInput.value.trim().length > 0) return;

        morph(this.refs.predictiveSearchResults, prepared.cloneNode(true));
        this.#resetScrollPositions();
      });
    }
  };
}

if (!customElements.get('predictive-search-component')) {
  customElements.define('predictive-search-component', PredictiveSearchComponent);
}
